import { getGroceriesPageName } from './getGroceriesPageName';
import { analytics } from './segment';
import { type AccessedFrom } from './types/AccessedFrom';
import { type Action } from './types/Action';
import {
  buildFeedInteractionPayload,
  type FeedInteractionFields,
} from './types/FeedInteractionFields';
import { type Location } from './types/Location';
import { resolveInteractionTab } from './utils/resolveInteractionTab';

type ProductInteractionParams = FeedInteractionFields & {
  productId: number;
  actionType: Action;
  productName?: string;
  location?: Location | null;
  orderId?: number;
  pageName?: string;
  conversationId?: string;
  /** Optional: surface the user came from before triggering this interaction. */
  accessedFrom?: AccessedFrom;
  /** Optional: present when the interaction originated from a search result. */
  searchId?: string;
  /** Optional: 0-indexed position in the search result list. */
  resultPosition?: number;
  /** Optional: interaction type (e.g. 'view') for events that use interaction_type alongside action_type. */
  interactionType?: string;
  /** Optional: whether the product is coming soon (out of stock but planned for restock). */
  comingSoon?: boolean;
  /** Optional: whether the product is out of stock. `null` when unavailable at the call site. */
  outOfStock?: boolean | null;
  /** Cookbook the product belongs to (when interaction surface is a cookbook). */
  cookbookId?: number;
  /** Display name of the cookbook the product belongs to. */
  cookbookName?: string;
  /** UI surface that contains the product. */
  componentType?: 'carousel' | 'card';
  /**
   * Identifier for the surface — for cookbook section carousels, this is the
   * snake-cased section title (e.g. 'high_protein_cookbooks_we_love').
   */
  componentName?: string;
  /** Layout the card was rendered in when the event fired. */
  componentLayout?: 'list' | 'grid';
  /**
   * 0-indexed position of the product within its surface (e.g. carousel slide
   * index).
   */
  objectIndex?: number;
  /**
   * Max index of the surface — i.e. (total - 1). For a carousel of 13 items
   * this is 12. Mirrors the semantics used by pairingInteraction.
   */
  objectTotal?: number;
  /** Personalized badge labels rendered for this product in the UI. */
  personalizedBadges?: string[];
  /** Whether personalized badges were rendered for this product in the UI. */
  badgeShown?: boolean;
};

export const productInteraction = ({
  productId,
  actionType,
  productName,
  location,
  orderId,
  pageName,
  conversationId,
  accessedFrom,
  searchId,
  resultPosition,
  interactionType,
  comingSoon,
  outOfStock,
  cookbookId,
  cookbookName,
  componentType,
  componentName,
  componentLayout,
  objectIndex,
  objectTotal,
  personalizedBadges,
  badgeShown,
  tab,
  tabSessionId,
  container,
  rankedListPosition,
  verticalPosition,
  horizontalPosition,
  contentType,
  recommendationSource,
  carouselId,
  carouselName,
  impressionId,
  cookbookOwnerId,
}: ProductInteractionParams) => {
  analytics.track('Product Interaction', {
    product_id: productId,
    product_name: productName,
    action_type: actionType,
    location,
    page_name: pageName ?? getGroceriesPageName(),
    order_id: orderId,
    client: 'web',
    conversation_id: conversationId,
    accessed_from: accessedFrom,
    search_id: searchId,
    result_position: resultPosition,
    ...(interactionType !== undefined && { interaction_type: interactionType }),
    coming_soon: comingSoon,
    out_of_stock: outOfStock,
    cookbook_id: cookbookId,
    cookbook_name: cookbookName,
    component_type: componentType,
    component_name: componentName,
    component_layout: componentLayout,
    object_index: objectIndex,
    object_total: objectTotal,
    ...(badgeShown !== undefined && { badge_shown: badgeShown }),
    ...(personalizedBadges !== undefined && {
      personalized_badges: personalizedBadges,
    }),
    ...buildFeedInteractionPayload({
      tab: resolveInteractionTab(tab),
      tabSessionId,
      container,
      rankedListPosition,
      verticalPosition,
      horizontalPosition,
      contentType,
      recommendationSource,
      carouselId,
      carouselName,
      impressionId,
      cookbookOwnerId,
    }),
  });
};
